import { useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import * as yup from 'yup'
import { useMutation } from '@apollo/client'
import { SUBMIT_EARLY_ACCESS_FORM_MUTATION } from '../../graphql/mutations'
import {
  Hero,
  PrimaryFeatures,
  SecondaryFeatures,
  Faqs,
  Testimonials,
  Pricing,
  SalientPageLayout,
  Container,
} from '../ui'

function LandingPage1() {
  const [submitForm] = useMutation(SUBMIT_EARLY_ACCESS_FORM_MUTATION)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    state: '',
    projects: '',
    recaptcha: ''
  })
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    state: '',
    projects: ''
  })
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null)

  const validationSchema = yup.object().shape({
    email: yup.string().required('Email address is required').email('Invalid email format'),
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    company: yup.string().required('Company name is required'),
    state: yup.string().required('State is required'),
    projects: yup.string().required('Number of projects is required'),
    recaptcha: yup.string().required('Please complete the reCAPTCHA')
  })

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.target as HTMLFormElement

    const formData = {
      email: form.email.value,
      firstName: form.firstName.value,
      lastName: form.lastName.value,
      company: form.company.value,
      state: form.state.value,
      projects: form.projects.value,
      recaptcha: recaptchaValue,
      _subject: 'New Early Access Form Submission',
      _template: 'table',
      _captcha: 'false'
    }

    const validationData = {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      company: formData.company,
      state: formData.state,
      projects: formData.projects,
      recaptcha: formData.recaptcha
    }

    try {
      await validationSchema.validate(validationData, { abortEarly: false })
      setValidationErrors({
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        state: '',
        projects: '',
        recaptcha: '',
      })

      await submitForm({
        variables: {
          input: {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            company: formData.company,
            state: formData.state,
            projects: formData.projects,
            recaptchaToken: formData.recaptcha
          }
        }
      })
      setFormSubmitted(true)
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors = {
          email: '',
          firstName: '',
          lastName: '',
          company: '',
          state: '',
          projects: '',
          recaptcha: '',
        }
        err.inner.forEach(error => {
          if (error.path) {
            (newErrors as Record<string, string>)[error.path] = error.message
          }
        })
        setValidationErrors(newErrors)
      }
      return
    }
    return true
  }

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value || '')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
 name, value 
} = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const renderSignupForm = () => {
    if (formSubmitted) {
      return (
        <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-6 rounded-lg shadow-lg" role="alert">
          <strong className="font-bold text-lg">Thank you for your interest!</strong>
          <p className="mt-2">We will be in touch soon.</p>
        </div>
      )
    }

    return (
      <form 
        onSubmit={handleFormSubmit} 
        role="form" 
        className="w-full bg-white text-slate-900 p-8 rounded-2xl shadow-xl shadow-slate-900/10"
      >
        <div className="mb-6">
          <label htmlFor="email" className="block text-left text-sm font-medium text-slate-700 mb-2">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="block w-full px-4 py-3 text-slate-900 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="you@company.com"
          />
          {validationErrors.email && <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>}
        </div>
        <div className="mb-6">
          <label htmlFor="firstName" className="block text-left text-sm font-medium text-slate-700 mb-2">First Name</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            className="block w-full px-4 py-3 text-slate-900 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="John"
          />
          {validationErrors.firstName && <p className="mt-1 text-sm text-red-600">{validationErrors.firstName}</p>}
        </div>
        <div className="mb-6">
          <label htmlFor="lastName" className="block text-left text-sm font-medium text-slate-700 mb-2">Last Name</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            className="block w-full px-4 py-3 text-slate-900 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Doe"
          />
          {validationErrors.lastName && <p className="mt-1 text-sm text-red-600">{validationErrors.lastName}</p>}
        </div>
        <div className="mb-6">
          <label htmlFor="company" className="block text-left text-sm font-medium text-slate-700 mb-2">Company Name</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            required
            className="block w-full px-4 py-3 text-slate-900 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Your Company LLC"
          />
          {validationErrors.company && <p className="mt-1 text-sm text-red-600">{validationErrors.company}</p>}
        </div>
        <div className="mb-6">
          <label htmlFor="state" className="block text-left text-sm font-medium text-slate-700 mb-2">State in which you perform work</label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
            required
            className="block w-full px-4 py-3 text-slate-900 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          >
            <option value="">Select a state</option>
            <option value="AL">Alabama</option>
            <option value="AK">Alaska</option>
            <option value="AZ">Arizona</option>
            <option value="AR">Arkansas</option>
            <option value="CA">California</option>
            <option value="CO">Colorado</option>
            <option value="CT">Connecticut</option>
            <option value="DE">Delaware</option>
            <option value="FL">Florida</option>
            <option value="GA">Georgia</option>
            <option value="HI">Hawaii</option>
            <option value="ID">Idaho</option>
            <option value="IL">Illinois</option>
            <option value="IN">Indiana</option>
            <option value="IA">Iowa</option>
            <option value="KS">Kansas</option>
            <option value="KY">Kentucky</option>
            <option value="LA">Louisiana</option>
            <option value="ME">Maine</option>
            <option value="MD">Maryland</option>
            <option value="MA">Massachusetts</option>
            <option value="MI">Michigan</option>
            <option value="MN">Minnesota</option>
            <option value="MS">Mississippi</option>
            <option value="MO">Missouri</option>
            <option value="MT">Montana</option>
            <option value="NE">Nebraska</option>
            <option value="NV">Nevada</option>
            <option value="NH">New Hampshire</option>
            <option value="NJ">New Jersey</option>
            <option value="NM">New Mexico</option>
            <option value="NY">New York</option>
            <option value="NC">North Carolina</option>
            <option value="ND">North Dakota</option>
            <option value="OH">Ohio</option>
            <option value="OK">Oklahoma</option>
            <option value="OR">Oregon</option>
            <option value="PA">Pennsylvania</option>
            <option value="RI">Rhode Island</option>
            <option value="SC">South Carolina</option>
            <option value="SD">South Dakota</option>
            <option value="TN">Tennessee</option>
            <option value="TX">Texas</option>
            <option value="UT">Utah</option>
            <option value="VT">Vermont</option>
            <option value="VA">Virginia</option>
            <option value="WA">Washington</option>
            <option value="WV">West Virginia</option>
            <option value="WI">Wisconsin</option>
            <option value="WY">Wyoming</option>
          </select>
          {validationErrors.state && <p className="mt-1 text-sm text-red-600">{validationErrors.state}</p>}
        </div>
        <div className="mb-6">
          <label htmlFor="projects" className="block text-left text-sm font-medium text-slate-700 mb-2">Number of public works projects</label>
          <input
            type="number"
            id="projects"
            name="projects"
            value={formData.projects}
            onChange={handleChange}
            required
            min="0"
            className="block w-full px-4 py-3 text-slate-900 bg-white border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="0"
          />
          {validationErrors.projects && <p className="mt-1 text-sm text-red-600">{validationErrors.projects}</p>}
        </div>
        <div className="mb-6">
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
            onChange={handleRecaptchaChange}
          />
          {validationErrors.recaptcha && <p className="mt-1 text-sm text-red-600">{validationErrors.recaptcha}</p>}
        </div>
        <div className="mt-8 mb-6">
          <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-opacity-50 text-lg shadow-lg hover:shadow-xl transition-all duration-300">Get Early Access & Updates</button>
        </div>
        <p className="text-xs text-slate-500">We respect your privacy. View our <a href="#privacy" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</a>.</p>
      </form>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SalientPageLayout>
        <Hero
          title="Stop dreading certified payroll. Start producing it in minutes."
          subtitle="Finally, a simple, guided tool that transforms complex certified payroll into a quick, error-free process."
          primaryAction={{
            text: "Get early access & updates",
            href: "#signup"
          }}
          highlightedText="in minutes"
        />

        <PrimaryFeatures />

        <SecondaryFeatures />

        <section id="signup" className="bg-slate-900 py-20 sm:py-32">
          <Container>
            <div className="mx-auto max-w-2xl md:text-center">
              <h2 className="font-display text-3xl tracking-tight text-white sm:text-4xl">
                Get Early Access
              </h2>
              <p className="mt-4 text-lg tracking-tight text-slate-400">
                Be among the first to experience Simple CPRs. Fill out the form below to join our early access program.
              </p>
            </div>
            <div className="mx-auto mt-16 max-w-lg">
              {renderSignupForm()}
            </div>
          </Container>
        </section>

        <Faqs />

        <Testimonials />

        <Pricing />
      </SalientPageLayout>
    </div>
  )
}

export default LandingPage1 